import { apiSlice } from './apiSlice';

export type ProductStatus = 'draft' | 'active' | 'archived';
export type ProductType =
  | 'simple'
  | 'variant_parent'
  | 'variant'
  | 'bundle'
  | 'service'
  | 'ticket'
  | 'subscription'
  | 'digital';

export interface CatalogProduct {
  id: string;
  name: string;
  slug: string;
  type: ProductType;
  status: ProductStatus;
  categoryId?: string | null;
  description?: string | null;
  currency?: string | null;
  unitPrice?: string | null;
  bundlePrice?: string | null;
  taxCode?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CursorPage<T> {
  data: T[];
  pagination: { cursor: string | null; hasMore: boolean; totalCount: number };
}

export interface Discount {
  id: string;
  name: string;
  kind: 'percent' | 'fixed' | 'bogo';
  discountKind: 'employee' | 'birthday' | 'student' | 'senior' | 'military' | 'promo' | 'membership';
  valueBps?: number | null;
  valueAmount?: string | null;
  bogoSpec?: { buy: number; get: number; selection: 'cheapest' | 'same-item' } | null;
  eligibility?: { productIds?: string[]; productKinds?: string[]; customerKinds?: string[] };
  redemptionCapOrg?: number | null;
  redemptionCapCustomer?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  status: 'draft' | 'active';
  createdAt?: string;
  updatedAt?: string;
}

export interface OrderMoney {
  amount: string;
  currency: string;
}

export interface OrderLine {
  id: string;
  productId?: string | null;
  variantId?: string | null;
  quantity: string;
  unitPrice: string;
  lineTotals?: Record<string, OrderMoney>;
}

export interface OrderListRecord {
  id: string;
  type: string;
  status: string;
  storeId: string;
  workspaceId: string;
  customerId?: string | null;
  channel: string;
  currency: string;
  totals?: { subtotal?: OrderMoney; discountTotal?: OrderMoney; taxTotal?: OrderMoney; feeTotal?: OrderMoney; total?: OrderMoney };
  receiptNumber?: string | null;
  createdAt: string;
  closedAt?: string | null;
  lines?: OrderLine[];
}

export interface OrderTenderSummary {
  id: string;
  seq: number;
  kind: string;
  amount: string;
  currency: string;
  gratuityAmount: string;
  gratuityMode?: string | null;
  status: string;
  refundedAmount: string;
  createdAt: string;
}

export interface OrderDetail extends OrderListRecord {
  tenderSummary: OrderTenderSummary[];
}

export interface OrderStatus {
  id: string;
  type: string;
  status: string;
  version: number;
  closedAt: string | null;
}

export interface OrderPage {
  data: OrderListRecord[];
  nextCursor: string | null;
}

export interface OrderTender {
  id: string;
  orderId: string;
  seq: number;
  kind: string;
  amount: string;
  currency: string;
  gratuityAmount: string;
  gratuityMode?: string | null;
  status: string;
  refundedAmount: string;
  createdAt: string;
}

export interface OrderTenderPage {
  data: OrderTender[];
  nextCursor: string | null;
}

export interface RefundOrderResult {
  id: string;
  status: string;
  settlement?: { status?: string };
}

export interface RefundPreview {
  amountMinor: string;
  currency: string;
  lines: Array<{ originalOrderLineId: string; quantity: string }>;
}

export interface ReportDescriptor {
  id: string;
  name: string;
  family: string;
  params: Array<{ name: string; label: string; type: string; required: boolean }>;
  fields: Array<{ name: string; label: string; type: string }>;
  slaMs: number;
  exportFormats: Array<'csv' | 'json'>;
}

export interface ReportRunResult {
  reportId: string;
  rows: Array<Record<string, unknown>>;
  nextCursor: string | null;
  durationMs: number;
}

export interface RunReportBody {
  params?: Record<string, string>;
  cursor?: string;
  limit?: number;
}

function safeOrderTender(tender: OrderTenderSummary): OrderTenderSummary {
  return {
    id: tender.id,
    seq: tender.seq,
    kind: tender.kind,
    amount: tender.amount,
    currency: tender.currency,
    gratuityAmount: tender.gratuityAmount,
    gratuityMode: tender.gratuityMode,
    status: tender.status,
    refundedAmount: tender.refundedAmount,
    createdAt: tender.createdAt,
  };
}

function safeTenderRecord(tender: OrderTender): OrderTender {
  return {
    id: tender.id,
    orderId: tender.orderId,
    seq: tender.seq,
    kind: tender.kind,
    amount: tender.amount,
    currency: tender.currency,
    gratuityAmount: tender.gratuityAmount,
    gratuityMode: tender.gratuityMode,
    status: tender.status,
    refundedAmount: tender.refundedAmount,
    createdAt: tender.createdAt,
  };
}

export const commerceP2cApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCatalogProducts: builder.query<CursorPage<CatalogProduct>, {
      cursor?: string;
      limit?: number;
      search?: string;
      status?: ProductStatus | 'all';
    }>({
      query: (params) => ({ url: '/commerce/v1/catalog/products', params }),
      providesTags: (result) => result
        ? [
            ...result.data.map(({ id }) => ({ type: 'CatalogProduct' as const, id })),
            { type: 'CatalogProduct', id: 'LIST' },
          ]
        : [{ type: 'CatalogProduct', id: 'LIST' }],
    }),
    getCatalogProduct: builder.query<CatalogProduct, string>({
      query: (id) => `/commerce/v1/catalog/products/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'CatalogProduct', id }],
    }),
    createCatalogProduct: builder.mutation<CatalogProduct, {
      idempotencyKey: string;
      body: Partial<CatalogProduct> & Pick<CatalogProduct, 'name' | 'slug' | 'type'>;
    }>({
      query: ({ idempotencyKey, body }) => ({
        url: '/commerce/v1/catalog/products',
        method: 'POST',
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: [{ type: 'CatalogProduct', id: 'LIST' }],
    }),
    updateCatalogProduct: builder.mutation<CatalogProduct, {
      id: string;
      idempotencyKey: string;
      body: Partial<CatalogProduct>;
    }>({
      query: ({ id, idempotencyKey, body }) => ({
        url: `/commerce/v1/catalog/products/${id}`,
        method: 'PATCH',
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'CatalogProduct', id },
        { type: 'CatalogProduct', id: 'LIST' },
      ],
    }),
    archiveCatalogProduct: builder.mutation<CatalogProduct, { id: string; idempotencyKey: string }>({
      query: ({ id, idempotencyKey }) => ({
        url: `/commerce/v1/catalog/products/${id}`,
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'CatalogProduct', id },
        { type: 'CatalogProduct', id: 'LIST' },
      ],
    }),
    getDiscounts: builder.query<Discount[], void>({
      query: () => '/commerce/v1/commerce/discounts',
      providesTags: (result) => result
        ? [...result.map(({ id }) => ({ type: 'Discount' as const, id })), { type: 'Discount', id: 'LIST' }]
        : [{ type: 'Discount', id: 'LIST' }],
    }),
    getDiscount: builder.query<Discount, string>({
      query: (id) => `/commerce/v1/commerce/discounts/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Discount', id }],
    }),
    createDiscount: builder.mutation<Discount, { idempotencyKey: string; body: Partial<Discount> & Pick<Discount, 'name' | 'kind' | 'discountKind' | 'eligibility'> }>({
      query: ({ idempotencyKey, body }) => ({
        url: '/commerce/v1/commerce/discounts',
        method: 'POST',
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: [{ type: 'Discount', id: 'LIST' }],
    }),
    updateDiscount: builder.mutation<Discount, { id: string; idempotencyKey: string; body: Partial<Discount> }>({
      query: ({ id, idempotencyKey, body }) => ({
        url: `/commerce/v1/commerce/discounts/${id}`,
        method: 'PATCH',
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Discount', id },
        { type: 'Discount', id: 'LIST' },
      ],
    }),
    archiveDiscount: builder.mutation<Discount, { id: string; idempotencyKey: string }>({
      query: ({ id, idempotencyKey }) => ({
        url: `/commerce/v1/commerce/discounts/${id}`,
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Discount', id },
        { type: 'Discount', id: 'LIST' },
      ],
    }),
    getOrders: builder.query<OrderPage, {
      cursor?: string;
      limit?: number;
      status?: string;
      search?: string;
      storeId?: string;
      workspaceId?: string;
      dateFrom?: string;
      dateTo?: string;
    }>({
      query: (params) => ({ url: '/orders/v1/orders', params }),
      providesTags: (result) => result
        ? [...result.data.map(({ id }) => ({ type: 'Order' as const, id })), { type: 'Order', id: 'LIST' }]
        : [{ type: 'Order', id: 'LIST' }],
    }),
    getOrder: builder.query<OrderDetail, string>({
      query: (id) => `/orders/v1/orders/${id}`,
      transformResponse: (order: OrderDetail) => ({
        ...order,
        tenderSummary: (order.tenderSummary ?? []).map(safeOrderTender),
      }),
      providesTags: (_result, _error, id) => [{ type: 'Order', id }],
    }),
    getOrderStatus: builder.query<OrderStatus, string>({
      query: (id) => `/orders/v1/orders/${id}/status`,
      providesTags: (_result, _error, id) => [{ type: 'Order', id }],
    }),
    getRelatedOrders: builder.query<Record<string, unknown>, string>({
      query: (id) => `/orders/v1/orders/${id}/related`,
      providesTags: (_result, _error, id) => [{ type: 'Order', id }],
    }),
    getOrderTenders: builder.query<OrderTenderPage, { orderId: string; cursor?: string; limit?: number }>({
      query: ({ orderId, ...params }) => ({
        url: `/payments/v1/payments/orders/${orderId}/tenders`,
        params,
      }),
      transformResponse: (page: OrderTenderPage) => ({
        ...page,
        data: page.data.map(safeTenderRecord),
      }),
      providesTags: (result, _error, { orderId }) => result
        ? [...result.data.map(({ id }) => ({ type: 'Tender' as const, id })), { type: 'Tender', id: `ORDER-${orderId}` }]
        : [{ type: 'Tender', id: `ORDER-${orderId}` }],
    }),
    previewRefundOrder: builder.mutation<RefundPreview, {
      orderId: string;
      lines: Array<{ originalOrderLineId: string; quantity: string }>;
    }>({
      query: ({ orderId, lines }) => ({
        url: `/orders/v1/orders/${encodeURIComponent(orderId)}/refund/preview`,
        method: 'POST',
        body: { lines },
      }),
    }),
    refundOrder: builder.mutation<RefundOrderResult, {
      orderId: string;
      lines: Array<{ originalOrderLineId: string; quantity: string }>;
      idempotencyKey: string;
    }>({
      query: ({ orderId, lines, idempotencyKey }) => ({
        url: `/orders/v1/orders/${encodeURIComponent(orderId)}/refund`,
        method: 'POST',
        body: { lines },
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        { type: 'Order', id: 'LIST' },
        { type: 'Tender', id: `ORDER-${orderId}` },
      ],
    }),
    getReportDescriptors: builder.query<ReportDescriptor[], void>({
      query: () => '/reporting/v1/reports',
    }),
    runReport: builder.mutation<ReportRunResult, { reportId: string; body: RunReportBody }>({
      query: ({ reportId, body }) => ({
        url: `/reporting/v1/reports/${reportId}/run`,
        method: 'POST',
        body,
      }),
    }),
    exportReport: builder.mutation<string, {
      reportId: string;
      format: 'csv' | 'json';
      params: RunReportBody['params'];
    }>({
      query: ({ reportId, format, params }) => ({
        url: `/reporting/v1/reports/${reportId}/export`,
        params: { format, params: JSON.stringify(params ?? {}) },
        responseHandler: 'text',
      }),
    }),
  }),
});

export const {
  useGetCatalogProductsQuery,
  useGetCatalogProductQuery,
  useCreateCatalogProductMutation,
  useUpdateCatalogProductMutation,
  useArchiveCatalogProductMutation,
  useGetDiscountsQuery,
  useGetDiscountQuery,
  useCreateDiscountMutation,
  useUpdateDiscountMutation,
  useArchiveDiscountMutation,
  useGetOrdersQuery,
  useGetOrderQuery,
  useGetOrderStatusQuery,
  useGetRelatedOrdersQuery,
  useGetOrderTendersQuery,
  usePreviewRefundOrderMutation,
  useRefundOrderMutation,
  useGetReportDescriptorsQuery,
  useRunReportMutation,
  useExportReportMutation,
} = commerceP2cApi;
